import { useMemo } from "react";
import { useFlags } from "flagsmith/react";
import {
  SplatCheckoutDocumentType,
  SplatODI,
} from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  Role,
  SplatContact,
  SplatLoan,
} from "../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { useCheckoutStepsData } from "checkout/api/checkoutSteps";
import { useContactsData } from "utils/swrHooks";
import { ROLES } from "utils/usageUtils";
import { useAuthToken } from "hooks/useAuthToken";
import { FeatureFlags } from "fixtures/features";
import { getContactValidationDocSigningLock } from "utils/contactValidation";
import { getPrimaryContact, getPrimarySignerContact } from "utils/contacts";
import { useLoans } from "checkout/hooks/useCheckoutSwr";

type ReadSignDocsProps = {
  prospectId: string;
  proposalId: string;
};

type ReadSignDocsStep = SplatODI & {
  docName: string;
  isUnlocked: boolean;
  lockedTooltip?: string;
  description: string;
  allowSecondaryContact: boolean;
  primarySignerRole: Role;
  documentType: SplatCheckoutDocumentType;
  showSMS?: boolean;
  showEmail?: boolean;
  disableEmail?: boolean;
  showRead?: boolean;
  showSign?: boolean;
  isOptional?: boolean;
  isUnknown?: boolean;
};

type ApplyContactValidationToStepOptions = {
  contacts?: SplatContact[];
  secondaryContact?: SplatContact;
  coborrower?: SplatContact;
  isBlockEnabled: boolean;
};

const applyContactValidationToStep = (
  step: ReadSignDocsStep,
  {
    contacts,
    secondaryContact,
    coborrower,
    isBlockEnabled,
  }: ApplyContactValidationToStepOptions
): ReadSignDocsStep => {
  if (!isBlockEnabled || step.isUnknown || !step.isUnlocked) {
    return step;
  }

  const signers: Array<SplatContact | undefined> = [
    getPrimarySignerContact(contacts, step.primarySignerRole),
  ];

  if (step.allowSecondaryContact) {
    signers.push(secondaryContact);
  }

  if (step.odiName === "loanDocumentSignature") {
    signers.push(coborrower);
  }

  const { isLocked, lockedTooltip } = getContactValidationDocSigningLock(
    signers,
    isBlockEnabled
  );

  if (!isLocked) {
    return step;
  }

  return {
    ...step,
    isUnlocked: false,
    lockedTooltip: step.lockedTooltip ?? lockedTooltip,
  };
};

const mapODIToDocs = (odi: SplatODI, allOdis: SplatODI[]) => {
  let isUnlocked = true;
  let docName = "";
  let description = "";
  let allowSecondaryContact = false;
  let primarySignerRole: Role;
  let documentType: SplatCheckoutDocumentType | undefined;
  let [showSign, showSMS, showEmail, disableEmail, showRead] =
    Array(5).fill(false);
  let isOptional = false;
  let isUnknown = false;
  const loanOdi = allOdis.find(
    (odi) => odi.odiName === "loanApplicationApproval"
  );
  const sunrunAgreementOdi = allOdis.find(
    (odi) => odi.odiName === "customerAgreement"
  );
  const cpucOdi = allOdis.find((odi) => odi.odiName === "cpuc");
  const intervalDataOdi = allOdis.find((odi) => odi.odiName === "intervalData");

  const ipaOdi = allOdis.find((odi) => odi.odiName === "ipaRebate");
  const creditCheckOdi = allOdis.find((odi) => odi.odiName === "creditCheck");

  switch (odi.odiName) {
    case "intervalData":
      docName = "Interval Data Decline Reason";
      primarySignerRole = ROLES.HOMEOWNER;
      description = "Interval Data Decline Reason";
      documentType = "Interval Data Decline Reason";
      isUnlocked = true;
      break;
    case "ipaRebate":
      docName = "Illinois Power Authority";
      primarySignerRole = ROLES.HOMEOWNER;
      description =
        "This document needs to be signed prior to signing the Sunrun Agreement. Please email the IPA document to the customer for signature.";
      documentType = "Illinois Power Authority";
      isUnlocked = true;
      showRead = false;
      showEmail = true;
      isOptional = false;
      break;
    case "hicLight":
      docName = "EV Charger Agreement";
      primarySignerRole = ROLES.HOMEOWNER;
      description =
        "Please review the terms of your EV Charger agreement with Sunrun.";
      documentType = "HIC Light";
      isUnlocked = !!sunrunAgreementOdi && sunrunAgreementOdi.completed;
      showRead = true;
      showEmail = true;
      isOptional = !odi.isRequired;
      break;

    case "cpuc":
      docName = "California Solar Consumer Protection Guide";
      primarySignerRole = ROLES.HOMEOWNER;
      description =
        "The state of California created a guide to outline the solar process, your rights, and address common questions.";
      documentType = "CPUC e-sign";
      showRead = true;
      showEmail = true;
      showSMS = true;
      isOptional = !odi.isRequired;
      isUnlocked = intervalDataOdi ? !!intervalDataOdi?.completed : true;
      break;

    case "nemAgreement":
      docName = "Net Metering Agreement";
      primarySignerRole = ROLES.UTILITYBILLCONTACT;
      description =
        "This agreement allows us to monitor your production and sell back the energy you have produced.";
      isUnlocked = !!sunrunAgreementOdi?.completed;

      documentType = "NEM Agreement";
      showRead = true;
      showEmail = true;
      showSMS = true;
      disableEmail = !sunrunAgreementOdi?.completed;
      isOptional = !odi.isRequired;
      break;

    case "loanDocumentSignature":
      docName = "Loan";
      primarySignerRole = ROLES.HOMEOWNER;
      description =
        "Please review the terms of your Loan agreement with the bank.";
      documentType = "Bank Approval Document";
      showEmail = true;

      isUnlocked = !(
        (!!loanOdi && !loanOdi.completed) ||
        (!!sunrunAgreementOdi && !sunrunAgreementOdi?.completed)
      );

      break;

    case "customerAgreement":
      docName = "Sunrun Agreement";
      description =
        "Please review the terms of your solar agreement with Sunrun.";
      allowSecondaryContact = true;
      primarySignerRole = ROLES.HOMEOWNER;
      isUnlocked = !(
        (!!ipaOdi && !ipaOdi.completed) ||
        (!!cpucOdi && !cpucOdi.completed) ||
        (!!creditCheckOdi && !creditCheckOdi.completed) ||
        (!!loanOdi && !loanOdi.completed)
      );

      documentType = "Customer Agreement";
      showRead = true;
      showSMS = true;
      showEmail = true;
      isOptional = !odi.isRequired;
      break;
    default:
      console.error(`Unknown ODI: ${odi.odiName}`);
      docName = odi.odiName;
      isUnknown = true;
      //these props don't really need to be here, but they can't be undefined
      documentType = "Home Owner Insurance";
      primarySignerRole = "Other";
  }

  return {
    ...odi,
    docName,
    description,
    isUnlocked,
    allowSecondaryContact,
    primarySignerRole,
    documentType,
    showEmail,
    showSMS,
    disableEmail,
    showRead,
    showSign,
    isOptional,
    isUnknown,
  };
};

const CREDIT_CHECK_LOCKED_TOOLTIP =
  "Credit check is not completed. Please complete the credit check before signing all required documents.";

const IPA_LOCKED_TOOLTIP =
  "The Illinois Power Authority (IPA) document must be signed before sending all documents.";

const useReadSignDocs = ({ prospectId, proposalId }: ReadSignDocsProps) => {
  const authKey = useAuthToken("HYBRID")?.token ?? "";
  const proposalToken = useAuthToken("PROPOSAL")?.token ?? "";
  const { steps, stepsError, isStepsLoading, refetch } = useCheckoutStepsData(
    prospectId,
    proposalId,
    authKey,
    proposalToken
  );
  const { contacts, isContactsLoading } = useContactsData(
    prospectId,
    authKey,
    proposalToken
  );
  const flags = useFlags([FeatureFlags.CONTACT_VALIDATION_BLOCK_DOC_SIGNING]);
  const isContactValidationBlockEnabled =
    flags[FeatureFlags.CONTACT_VALIDATION_BLOCK_DOC_SIGNING]?.enabled ?? false;

  const allOdIs = steps?.reduce(
    (acc, step) => [...acc, ...step.odis],
    [] as SplatODI[]
  );

  const needsLoanData = useMemo(
    () =>
      allOdIs?.some((odi) => odi.odiName === "loanDocumentSignature") ?? false,
    [allOdIs]
  );

  const { loans } = useLoans(
    prospectId,
    authKey,
    false,
    proposalToken,
    needsLoanData
  );

  const secondaryContact = contacts?.find((contact) =>
    contact.derivedRoles.includes(ROLES.CONTRACTCOSIGNER)
  );

  const coborrower = useMemo(() => {
    const approvedLoan: SplatLoan | undefined = loans?.find(
      (loan) => loan.Credit_Results__c === "Approved"
    );

    return contacts?.find(
      (contact) => contact.contactId === approvedLoan?.Secondary_Loan_Signer__c
    );
  }, [contacts, loans]);

  const readSignDocsSteps = useMemo(() => {
    const mappedSteps = steps
      ?.filter((step) =>
        ["readAndSignDocs", "nemAgreement", "intervalData"].includes(step.name)
      )
      .reduce((acc, step) => {
        return [...acc, ...step.odis.map((odi) => mapODIToDocs(odi, allOdIs!))];
      }, [] as ReadSignDocsStep[]);

    return mappedSteps?.map((step) =>
      applyContactValidationToStep(step, {
        contacts,
        secondaryContact,
        coborrower,
        isBlockEnabled: isContactValidationBlockEnabled,
      })
    );
  }, [
    allOdIs,
    coborrower,
    contacts,
    isContactValidationBlockEnabled,
    secondaryContact,
    steps,
  ]);

  const combinedDocsLock = useMemo(() => {
    const creditCheckOdi = allOdIs?.find(
      (odi) => odi.odiName === "creditCheck"
    );
    const isCreditCheckComplete = creditCheckOdi?.completed ?? true;

    if (!isCreditCheckComplete) {
      return {
        isUnlocked: false,
        lockedTooltip: CREDIT_CHECK_LOCKED_TOOLTIP,
      };
    }

    const ipaOdi = allOdIs?.find((odi) => odi.odiName === "ipaRebate");
    if (ipaOdi && !ipaOdi.completed) {
      return {
        isUnlocked: false,
        lockedTooltip: IPA_LOCKED_TOOLTIP,
      };
    }

    const { isLocked, lockedTooltip } = getContactValidationDocSigningLock(
      [getPrimaryContact(contacts), secondaryContact],
      isContactValidationBlockEnabled
    );

    if (isLocked) {
      return {
        isUnlocked: false,
        lockedTooltip,
      };
    }

    return {
      isUnlocked: true,
      lockedTooltip: undefined,
    };
  }, [allOdIs, contacts, isContactValidationBlockEnabled, secondaryContact]);

  return {
    allOdIs,
    readSignDocsSteps,
    combinedDocsLock,
    coborrower,
    stepsError,
    isStepsLoading: isStepsLoading || isContactsLoading,
    refetch,
  };
};

export {
  useReadSignDocs,
  mapODIToDocs,
  applyContactValidationToStep,
  CREDIT_CHECK_LOCKED_TOOLTIP,
  IPA_LOCKED_TOOLTIP,
};
export type { ReadSignDocsStep };
